import { useEffect, useRef, useState, type JSX } from 'react';

import { WELCOME_SNOOZE_DAYS_DEFAULT } from '@bookora/shared';
import { Button, Coachmark, Icon, Modal, Typography, topmostOverlay } from '@bookora/ui';
import { createPortal } from 'react-dom';

import { useAuth } from '@/auth/token-store';

import { useGuidance, useRecordGuidance } from './api';
import { useStepContext } from './context';
import {
  ANNOUNCEMENT_KEY,
  FIRST_RUN_VERSION,
  WELCOME_KEY,
  firstRunSettled,
  firstRunSurface,
  isSetupWizard,
} from './eligibility';
import { useCollapseWhenIgnored, waitForQuiet } from './quiet';
import { openStepForLocation, useSetupProgress } from './setup-progress';
import { findItem } from './state';
import { noteProactiveShown, readCoreSeen, readLastOffer, tabGuardAllows } from './storage';
import { QUIET_MS, WELCOME_DELAY_MS, WELCOME_MAX_WAIT_MS } from './timing';
import { useTourStore } from './tour-store';
import { COPY } from './tours/copy';
import { startTour } from './runtime';

function routeSettled(): boolean {
  return document.querySelector('#bk-main .bk-route-fallback') === null;
}

function conditionsHold(): boolean {
  return document.visibilityState === 'visible' && topmostOverlay() === null && routeSettled();
}

/**
 * The first-run surface, once per staff member (user × clinic):
 *
 *  · NEWCOMER → the Welcome modal, the ONE interruption this system makes;
 *  · VETERAN  → a quiet announcement callout beside the Help entry.
 *
 * Waits until the state is known, the tab is visible, no overlay is open, the
 * route's Suspense has settled and the user is not on the signup wizard; then
 * 800 ms plus 1.2 s of input quiet, giving up at 10 s and trying again on the
 * next page. Never while impersonating. While undecided, `useGuidanceBusy()`
 * is true, so nothing else proactive gets in first.
 *
 * A brand-new owner mid-setup is not interrupted on a setup step's own page
 * either: the checklist's «انجامش بده» opens it, and a modal over it would
 * cover exactly what they came to do. The welcome waits (postponed, not
 * snoozed) for a page that is not a step — the dashboard, usually.
 */
export function FirstRun(): JSX.Element | null {
  const guidance = useGuidance();
  const { impersonatedBy } = useAuth();
  const pathname = useTourStore((s) => s.pathname);
  const search = useTourStore((s) => s.search);
  const welcome = useTourStore((s) => s.welcome);
  const setWelcome = useTourStore((s) => s.setWelcome);
  const setAnnouncementOpen = useTourStore((s) => s.setAnnouncementOpen);
  const announcementOpen = useTourStore((s) => s.announcementOpen);
  const record = useRecordGuidance();
  const ctx = useStepContext();
  const setup = useSetupProgress();
  const [surface, setSurface] = useState<'welcome' | 'announcement' | null>(null);

  const data = guidance.data;
  // A new owner on an open setup step's page: postponed like the wizard.
  const onSetupStep =
    ctx.ownerVariant === 'new-owner' &&
    setup.visible &&
    openStepForLocation(setup.steps, pathname, search) !== null;

  // The announcement was answered or ignored: first-run is settled here.
  useEffect(() => {
    if (surface === 'announcement' && !announcementOpen) {
      setSurface(null);
      setWelcome('resolved');
    }
  }, [surface, announcementOpen, setWelcome]);

  // Decide, on every page and every state change, what (if anything) is due.
  useEffect(() => {
    if (welcome === 'open') return;
    if (impersonatedBy || data?.readOnly || guidance.isError) {
      setWelcome('resolved');
      return;
    }
    if (!data) {
      setWelcome('unknown');
      return;
    }
    const now = new Date();
    const welcomeItem = findItem(data, 'WELCOME', WELCOME_KEY);
    const announcementItem = findItem(data, 'WELCOME', ANNOUNCEMENT_KEY);
    const profile = {
      ...data.profile,
      autoOfferPausedUntil: data.profile.autoOfferPausedUntil ?? null,
      lastProactiveAt: data.profile.lastProactiveAt ?? null,
      proactiveDayKey: data.profile.proactiveDayKey ?? null,
      tipsPausedUntil: data.profile.tipsPausedUntil ?? null,
      newTipsDayKey: data.profile.newTipsDayKey ?? null,
      tipBackoffUntil: data.profile.tipBackoffUntil ?? null,
    };
    if (firstRunSettled(profile, welcomeItem, announcementItem, now)) {
      setWelcome('resolved');
      return;
    }
    const onSetupWizard = isSetupWizard(pathname, search) || onSetupStep;
    const due = firstRunSurface({
      now,
      profile,
      impersonating: false,
      localLastOffer: readLastOffer(),
      tabGuardAllows: tabGuardAllows(now),
      welcome: welcomeItem,
      announcement: announcementItem,
      onSetupWizard,
    });
    if (!due) {
      // The wizard only postpones the welcome (not a snooze); a spent budget
      // or this tab's daily guard settles it for this page.
      setWelcome(onSetupWizard ? 'pending' : 'resolved');
      return;
    }
    setWelcome('pending');

    const controller = new AbortController();
    void (async () => {
      const startedAt = Date.now();
      // Wait for the page to be ready and nothing else to be on top of it.
      while (!conditionsHold()) {
        if (controller.signal.aborted) return;
        if (Date.now() - startedAt > WELCOME_MAX_WAIT_MS) {
          setWelcome('resolved');
          return;
        }
        await new Promise((r) => setTimeout(r, 200));
      }
      await new Promise((r) => setTimeout(r, WELCOME_DELAY_MS));
      if (controller.signal.aborted) return;
      const remaining = Math.max(0, WELCOME_MAX_WAIT_MS - (Date.now() - startedAt));
      const quiet = await waitForQuiet(QUIET_MS, remaining, controller.signal);
      if (controller.signal.aborted) return;
      if (!quiet || !conditionsHold()) {
        setWelcome('resolved');
        return;
      }
      const shownAt = new Date();
      noteProactiveShown(shownAt);
      record({
        type: 'OFFERED',
        kind: 'WELCOME',
        key: due === 'welcome' ? WELCOME_KEY : ANNOUNCEMENT_KEY,
        version: FIRST_RUN_VERSION,
        source: 'auto',
      });
      setSurface(due);
      if (due === 'announcement') setAnnouncementOpen(true);
      setWelcome('open');
    })();
    return () => controller.abort();
    // `welcome` is written here; re-running on it would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, guidance.isError, impersonatedBy, pathname, search, onSetupStep]);

  if (surface === 'welcome') {
    return (
      <WelcomeDialog
        onDone={() => {
          setSurface(null);
          setWelcome('resolved');
        }}
      />
    );
  }
  return null;
}

function WelcomeDialog({ onDone }: { onDone: () => void }): JSX.Element {
  const record = useRecordGuidance();
  const ctx = useStepContext();
  const { user, activeClinic } = useAuth();
  const [open, setOpen] = useState(true);
  const accepted = useRef(false);
  const anotherClinic = readCoreSeen(user?.id);

  const input = {
    name: user?.fullName ?? null,
    clinicName: activeClinic?.clinicName ?? null,
    roleName: activeClinic?.roleName ?? null,
    anotherClinic,
  };
  const title =
    anotherClinic && activeClinic?.clinicName
      ? COPY.welcome.titleAnotherClinic(activeClinic.clinicName)
      : COPY.welcome.title;
  const footnote =
    ctx.viewport === 'compact' ? COPY.welcome.footnoteCompact : COPY.welcome.footnoteWide;

  const snooze = (): void => {
    if (!open) return;
    record({
      type: 'SNOOZED',
      kind: 'WELCOME',
      key: WELCOME_KEY,
      version: FIRST_RUN_VERSION,
      snoozeDays: WELCOME_SNOOZE_DAYS_DEFAULT,
      source: 'welcome',
    });
    setOpen(false);
  };

  const accept = (): void => {
    accepted.current = true;
    record({
      type: 'COMPLETED',
      kind: 'WELCOME',
      key: WELCOME_KEY,
      version: FIRST_RUN_VERSION,
      source: 'welcome',
    });
    setOpen(false);
  };

  // The heading is the Modal's own title, so the dialog is NAMED by it
  // (`aria-labelledby`); `classNames` keep its look.
  return (
    <Modal
      open={open}
      width={440}
      centered
      closable
      onCancel={snooze}
      footer={null}
      title={
        <>
          <span className="bk-guidance-welcome__icon" aria-hidden="true">
            <Icon name="help" size={24} />
          </span>
          <span className="bk-guidance-welcome__title">{title}</span>
        </>
      }
      classNames={{
        header: 'bk-guidance-welcome__header',
        title: 'bk-guidance-welcome__heading',
      }}
      afterClose={() => {
        onDone();
        // The core tour starts once the modal has left: a modal dialog on
        // screen would block the tour's first step.
        if (accepted.current) startTour('core', { source: 'welcome' });
      }}
      className="bk-guidance-welcome"
    >
      {activeClinic?.roleName ? (
        <Typography.Text type="secondary" className="bk-guidance-welcome__role">
          {COPY.welcome.roleLine(activeClinic.roleName)}
        </Typography.Text>
      ) : null}
      <Typography.Paragraph className="bk-guidance-welcome__body">
        {COPY.welcome.body(ctx, input)}
      </Typography.Paragraph>
      <div className="bk-guidance-welcome__actions">
        <Button onClick={snooze}>{COPY.welcome.secondary}</Button>
        <Button type="primary" onClick={accept}>
          {COPY.welcome.primary}
        </Button>
      </div>
      <Typography.Paragraph type="secondary" className="bk-guidance-welcome__footnote">
        {footnote}
      </Typography.Paragraph>
    </Modal>
  );
}

/**
 * The veteran announcement, as the Help trigger's next sibling (wide), or
 * pinned under the account menu (compact, where the Help entry lives in that
 * menu). Non-modal: it takes no focus and collapses when ignored.
 */
export function Announcement({
  placement,
}: {
  placement: 'sibling' | 'compact';
}): JSX.Element | null {
  const open = useTourStore((s) => s.announcementOpen);
  const setOpen = useTourStore((s) => s.setAnnouncementOpen);
  const record = useRecordGuidance();
  const openHub = useTourStore((s) => s.openHub);

  useCollapseWhenIgnored(open, 'bk-guidance-announcement', () => setOpen(false));

  if (!open) return null;

  const node = (
    <Coachmark
      open
      mode="offer"
      id="bk-guidance-announcement"
      step={{
        id: ANNOUNCEMENT_KEY,
        title: COPY.announcement.title,
        body: placement === 'compact' ? COPY.announcement.bodyCompact : COPY.announcement.bodyWide,
        target: null,
      }}
      index={0}
      total={1}
      primaryLabel={COPY.announcement.primary}
      secondaryLabel={COPY.announcement.secondary}
      onAccept={() => {
        record({
          type: 'COMPLETED',
          kind: 'WELCOME',
          key: ANNOUNCEMENT_KEY,
          version: FIRST_RUN_VERSION,
          source: 'offer',
        });
        setOpen(false);
        openHub(null, 'offer');
      }}
      onDecline={() => {
        record({
          type: 'DISMISSED',
          kind: 'WELCOME',
          key: ANNOUNCEMENT_KEY,
          version: FIRST_RUN_VERSION,
          source: 'offer',
        });
        setOpen(false);
      }}
      onDismiss={() => setOpen(false)}
      onNext={() => undefined}
      onPrev={() => undefined}
      onFinish={() => undefined}
    />
  );

  if (placement === 'sibling') return node;
  return createPortal(<div className="bk-guidance-compact-callout">{node}</div>, document.body);
}
